// Real onboarding API — wires the 8-step flow to the backend endpoints.
// Keeps the same exported names the screens already use (verifyOtp, resendOtp,
// submitApplication, getApplicantStatus, OnboardingApiError) so screens swap
// cleanly off the old mock layer.
import type {
  IdentityCaptures,
  KnowYouDetails,
  OnboardingDraft,
  PersonalDetails,
  SubmitResult,
} from '@/types/onboarding.types';
import { ApiError, del, get, isRealApiEnabled, patch, post } from './client';
import { getApplicationId, getSession, setApplicationId, setSession } from './session';
import { MAX_REQUEST_BYTES } from '@/utils/imageCompress';

// Re-export so screens can catch OnboardingApiError without changing imports.
export { ApiError as OnboardingApiError };

// ---- Backend shapes ----
export interface ApplicationData {
  applicationId: string;
  applicationReference: string;
  customerId: string;
  status: string;
  accountType: string;
  currentStep: number;
  fullName: string;
  returningCustomer: boolean;
  maskedMobile: string;
  maskedEmail: string;
  email?: string;
  maskedAadhaar: string;
  maskedPan: string;
  mobileVerified: boolean;
  emailVerified: boolean;
  otpExpiresAt?: string;
  uploadedDocuments: string[];
  signatureCaptured: boolean;
  profileCompleted: boolean;
  submittedAt?: string;
  updatedAt?: string;
  // Present once the bank approves and opens the account.
  accountNumber?: string;
  ifsc?: string;
  branchName?: string;
  lifecycleState?: string;
  kycLevel?: string;
  activatedAt?: string;
}

export interface TrackedApplication {
  applicationId: string;
  reference: string;
  customerId: string;
  product: string;
  status: string;
  currentStep: number;
  fullName: string;
  firstName: string;
  maskedMobile: string;
  maskedEmail: string;
  mobileVerified: boolean;
  emailVerified: boolean;
  signatureCaptured: boolean;
  profileCompleted: boolean;
  uploadedDocuments: string[];
  submittedAt?: string;
  accountNumber?: string;
  ifsc?: string;
  branchName?: string;
  activatedAt?: string;
}

interface SubmitData {
  applicationReference: string;
  customerId: string;
  accountType: string;
  registeredMobile: string;
  applicantName: string;
  submittedAt: string;
}

const OVR = '/onboarding/applications';

const requireApplicationId = (): string => {
  const id = getApplicationId();
  if (!id) {
    throw new ApiError('NO_APPLICATION', 'Your application session has expired. Please start again.');
  }
  return id;
};

const MOCK_APPLICATION_KEY = 'nova_mock_onboarding_application';

const mockReadApplication = (): ApplicationData | null => {
  try {
    const raw = localStorage.getItem(MOCK_APPLICATION_KEY);
    return raw ? (JSON.parse(raw) as ApplicationData) : null;
  } catch {
    return null;
  }
};

const mockSaveApplication = (application: ApplicationData): ApplicationData => {
  localStorage.setItem(MOCK_APPLICATION_KEY, JSON.stringify(application));
  setApplicationId(application.applicationId);
  setSession({
    type: 'TRACKING',
    reference: application.applicationReference,
    applicationId: application.applicationId,
    customerId: application.customerId,
    mobileNumber: application.maskedMobile,
  });
  return application;
};

export const getMockOnboardingApplication = (): ApplicationData | null => mockReadApplication();

export const updateMockOnboardingApplication = (
  changes: Partial<ApplicationData>,
): ApplicationData => {
  const current = mockReadApplication();
  if (!current) throw new ApiError('NO_APPLICATION', 'No mock application exists.');
  return mockSaveApplication({ ...current, ...changes, updatedAt: new Date().toISOString() });
};

const mockApplicationFromInput = (input: CreateApplicationInput): ApplicationData => {
  const p = input.personal;
  const now = new Date().toISOString();
  const id = `mock-app-${Date.now()}`;
  const reference = `MOCK-${String(Date.now()).slice(-6)}`;
  const fullName = `${p.firstAndMiddleNames.trim()} ${p.surname.trim()}`.trim();
  const mobile = p.mobile.replace(/\D/g, '');
  return mockSaveApplication({
    applicationId: id,
    applicationReference: reference,
    customerId: `CUS-${String(Date.now()).slice(-8)}`,
    status: 'STARTED',
    accountType: input.product,
    currentStep: 2,
    fullName,
    returningCustomer: false,
    maskedMobile: mobile ? `+91 ${mobile.slice(0, 2)}xxx xx${mobile.slice(-2)}` : '',
    maskedEmail: p.email ? `${p.email.slice(0, 2)}***@${p.email.split('@')[1] ?? 'example.test'}` : '',
    email: p.email.trim(),
    maskedAadhaar: `XXXX XXXX ${p.aadhaar.replace(/\D/g, '').slice(-4)}`,
    maskedPan: p.pan ? `${p.pan.slice(0, 2)}***${p.pan.slice(-2)}` : '',
    mobileVerified: false,
    emailVerified: false,
    uploadedDocuments: [],
    signatureCaptured: false,
    profileCompleted: false,
    updatedAt: now,
  });
};

// Map a backend error code to a friendly message, with sensible fallbacks.
const friendly = (code: string, message: string): string => {
  switch (code) {
    case 'AUTH_012':
      return 'The code you entered is incorrect. Please try again.';
    case 'AUTH_017':
      return 'That code has expired. Please resend a new one.';
    case 'AUTH_020':
      return 'Too many attempts. Please resend the code.';
    case 'CUS_010':
      return 'An application for this account type already exists for this Aadhaar.';
    case 'BANK_014':
      return 'This account type is not currently offered.';
    case 'ERR_006':
      return message;
    default:
      return message;
  }
};

const wrap = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof ApiError) {
      throw new ApiError(error.code, friendly(error.code, error.message), error.status);
    }
    throw error;
  }
};

// ---- Personal details (steps 1-2) → create application, sends both OTPs ----
export interface CreateApplicationInput {
  product: string;
  personal: PersonalDetails;
  /** From the consent pop-up on the product screen — the backend refuses the
   *  create without the terms + privacy consent. */
  consentsAccepted: boolean;
}

export const createApplication = async (
  input: CreateApplicationInput
): Promise<ApplicationData> => {
  const p = input.personal;
  if (!isRealApiEnabled()) return Promise.resolve(mockApplicationFromInput(input));
  const body = {
    accountType: input.product,
    salutation: p.salutation.toUpperCase(),
    firstAndMiddleName: p.firstAndMiddleNames.trim(),
    surname: p.surname.trim(),
    dateOfBirth: `${p.dobYear}-${p.dobMonth.padStart(2, '0')}-${p.dobDay.padStart(2, '0')}`,
    gender: (p.gender || 'OTHER').toUpperCase(),
    aadhaarNumber: p.aadhaar.replace(/\s/g, ''),
    panNumber: p.pan.trim().toUpperCase(),
    email: p.email.trim(),
    mobileNumber: p.mobile.startsWith('+') ? p.mobile.replace(/\s/g, '') : `+91${p.mobile.replace(/\D/g, '')}`,
    residentOfIndia: p.residentOfIndia === 'YES',
    consents: input.consentsAccepted
      ? [
          { type: 'TERMS', accepted: true },
          { type: 'PRIVACY', accepted: true },
        ]
      : [],
  };
  return wrap(() => post<ApplicationData>(OVR, body));
};

// ---- OTP verify (steps 3-4) ----
// target encodes which channel: 'sms' | 'email'
export const verifyOtp = async (channel: 'sms' | 'email', code: string): Promise<ApplicationData> => {
  const id = requireApplicationId();
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    if (!application || code !== '123456') throw new ApiError('AUTH_012', 'The code you entered is incorrect. Please try again.');
    return Promise.resolve(mockSaveApplication({ ...application, mobileVerified: application.mobileVerified || channel === 'sms', emailVerified: application.emailVerified || channel === 'email', currentStep: Math.max(application.currentStep, channel === 'email' ? 4 : 3), status: 'CONTACT_VERIFIED' }));
  }
  const body = channel === 'sms' ? { mobileOtp: code } : { emailOtp: code };
  return wrap(() => post<ApplicationData>(`${OVR}/${id}/verify-contact`, body));
};

export const resendOtp = async (channel: 'sms' | 'email'): Promise<ApplicationData> => {
  const id = requireApplicationId();
  if (!isRealApiEnabled()) return Promise.resolve(mockReadApplication() ?? (() => { throw new ApiError('NO_APPLICATION', 'No mock application exists.'); })());
  const query = channel === 'sms' ? 'SMS' : 'EMAIL';
  return wrap(() => post<ApplicationData>(`${OVR}/${id}/resend-otp?channel=${query}`));
};

// ---- Documents (step 5) — multipart, all four in one request ----
const dataUrlToBlob = (dataUrl: string): Blob => {
  const [meta, base64] = dataUrl.split(',');
  const mime = /data:(.*?)(;|$)/.exec(meta)?.[1] || 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

// The server's nginx refuses request bodies over 1 MB with HTTP 413. The
// photos are shrunk when they are taken (utils/imageCompress), so this only
// shows if they are still too big.
export const PHOTOS_TOO_LARGE_MESSAGE =
  'These photos are too large to send. Please retake them and try again.';
// Room left for the multipart part headers.
const MULTIPART_HEADROOM_BYTES = 16 * 1024;

/** True when the server refused the request body as too large. */
export const isPayloadTooLarge = (error: unknown): boolean =>
  error instanceof ApiError &&
  (error.status === 413 ||
    error.code === 'HTTP_413' ||
    /payload too large|entity too large|maximum upload size/i.test(error.message));

export const uploadDocuments = async (
  captures: Required<Pick<IdentityCaptures, 'aadhaarFront' | 'aadhaarBack' | 'panCard' | 'selfie'>>
): Promise<ApplicationData> => {
  const id = requireApplicationId();
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    if (!application) throw new ApiError('NO_APPLICATION', 'No mock application exists.');
    return Promise.resolve(mockSaveApplication({ ...application, uploadedDocuments: ['AADHAAR_FRONT', 'AADHAAR_BACK', 'PAN_CARD', 'SELFIE'], currentStep: Math.max(application.currentStep, 5), status: 'DOCUMENTS_UPLOADED' }));
  }
  const files = {
    aadhaarFront: dataUrlToBlob(captures.aadhaarFront),
    aadhaarBack: dataUrlToBlob(captures.aadhaarBack),
    panCard: dataUrlToBlob(captures.panCard),
    selfie: dataUrlToBlob(captures.selfie),
  };
  const totalBytes = Object.values(files).reduce((sum, blob) => sum + blob.size, 0);
  // Don't send a body the server would refuse anyway.
  if (totalBytes + MULTIPART_HEADROOM_BYTES > MAX_REQUEST_BYTES) {
    throw new ApiError('PAYLOAD_TOO_LARGE', PHOTOS_TOO_LARGE_MESSAGE, 413);
  }
  const form = new FormData();
  form.append('aadhaarFront', files.aadhaarFront, 'aadhaarFront.jpg');
  form.append('aadhaarBack', files.aadhaarBack, 'aadhaarBack.jpg');
  form.append('panCard', files.panCard, 'panCard.jpg');
  form.append('selfie', files.selfie, 'selfie.jpg');
  try {
    return await wrap(() =>
      post<ApplicationData>(`${OVR}/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  } catch (error) {
    if (isPayloadTooLarge(error)) {
      throw new ApiError('PAYLOAD_TOO_LARGE', PHOTOS_TOO_LARGE_MESSAGE, 413);
    }
    throw error;
  }
};

// ---- Signature (step 6) ----
export const uploadSignature = async (signaturePng: string): Promise<ApplicationData> => {
  const id = requireApplicationId();
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    if (!application) throw new ApiError('NO_APPLICATION', 'No mock application exists.');
    return Promise.resolve(mockSaveApplication({ ...application, signatureCaptured: true, currentStep: Math.max(application.currentStep, 6), status: 'SIGNATURE_CAPTURED' }));
  }
  const application = await wrap(() =>
    post<ApplicationData>(`${OVR}/${id}/signature`, { signatureImageBase64: signaturePng })
  );
  if (!application.signatureCaptured) {
    throw new ApiError('ERR_006', 'The signature was not saved. Please sign again.');
  }
  return application;
};

// ---- Profile (step 7) — map KnowYouDetails → backend enums ----
const MARITAL: Record<string, string> = {
  Single: 'SINGLE', Married: 'MARRIED', Divorced: 'DIVORCED', Widowed: 'WIDOWED', Separated: 'SEPARATED',
};
const RESIDENCE: Record<string, string> = {
  Owned: 'OWNED', Rented: 'RENTED', 'Company provided': 'COMPANY_PROVIDED', Parental: 'PARENTAL', Other: 'OTHER',
};
const YEARS: Record<string, string> = {
  'Less than 1': 'LESS_THAN_1', '1–3': 'ONE_TO_THREE', '3–5': 'THREE_TO_FIVE', '5 or more': 'FIVE_OR_MORE',
};
const EMPLOYMENT: Record<string, string> = {
  'Salaried (private)': 'SALARIED_PRIVATE',
  'Salaried (government/PSU)': 'SALARIED_GOVERNMENT',
  'Self-employed professional': 'SELF_EMPLOYED_PROFESSIONAL',
  'Business owner': 'BUSINESS_OWNER',
  Student: 'STUDENT', Homemaker: 'HOMEMAKER', Retired: 'RETIRED', Unemployed: 'UNEMPLOYED',
};
const INCOME: Record<string, string> = {
  'Up to ₹2.5 lakh': 'UPTO_2_5_LAKH',
  '₹2.5–5 lakh': 'FROM_2_5_TO_5_LAKH',
  '₹5–10 lakh': 'FROM_5_TO_10_LAKH',
  '₹10–25 lakh': 'FROM_10_TO_25_LAKH',
  '₹25 lakh–1 crore': 'FROM_25_LAKH_TO_1_CRORE',
  'Above ₹1 crore': 'ABOVE_1_CRORE',
};
const FUNDS: Record<string, string> = {
  Salary: 'SALARY', 'Business income': 'BUSINESS_INCOME', 'Professional fees': 'PROFESSIONAL_FEES',
  Savings: 'SAVINGS', 'Interest/dividend': 'INTEREST_DIVIDEND', 'Rental income': 'RENTAL_INCOME',
  Agriculture: 'AGRICULTURE', Pension: 'PENSION', 'Family support': 'FAMILY_SUPPORT', Other: 'OTHER',
};
const PURPOSE: Record<string, string> = {
  Savings: 'SAVINGS', 'Salary credit': 'SALARY_CREDIT', 'Business transactions': 'BUSINESS_TRANSACTIONS',
  Investments: 'INVESTMENTS', 'Receiving family support': 'RECEIVING_FAMILY_SUPPORT', Pension: 'PENSION', Other: 'OTHER',
};
const DEPOSITS: Record<string, string> = {
  'Up to ₹50,000': 'UPTO_50K',
  '₹50,000–2 lakh': 'FROM_50K_TO_2_LAKH',
  '₹2–5 lakh': 'FROM_2_TO_5_LAKH',
  '₹5–10 lakh': 'FROM_5_TO_10_LAKH',
  'Above ₹10 lakh': 'ABOVE_10_LAKH',
};
const QUALIFICATION: Record<string, string> = {
  'Below 10th': 'BELOW_10TH', '10th': 'TENTH', '12th': 'TWELFTH', Graduate: 'GRADUATE',
  Postgraduate: 'POSTGRADUATE', Professional: 'PROFESSIONAL', Other: 'OTHER',
};
// Contract enum — one value per UI label (do NOT collapse to CHILD/PARENT).
const RELATIONSHIP: Record<string, string> = {
  Spouse: 'SPOUSE', Son: 'SON', Daughter: 'DAUGHTER', Father: 'FATHER', Mother: 'MOTHER',
  Brother: 'BROTHER', Sister: 'SISTER', Other: 'OTHER',
};
const INDUSTRY: Record<string, string> = {
  'IT/ITES': 'IT_ITES', 'Banking/Finance': 'BANKING_FINANCE', Manufacturing: 'MANUFACTURING',
  Trading: 'TRADING', Healthcare: 'HEALTHCARE', Education: 'EDUCATION', Government: 'GOVERNMENT',
  Agriculture: 'AGRICULTURE', 'Real estate': 'REAL_ESTATE', 'Jewellery/Bullion': 'JEWELLERY_BULLION',
  'Arms/Defence': 'ARMS_DEFENCE', 'Money services': 'MONEY_SERVICES', 'Crypto/VDA': 'CRYPTO_VDA',
  Other: 'OTHER',
};
const PEP_REL: Record<string, string> = {
  Self: 'SELF', Family: 'FAMILY', 'Close associate': 'CLOSE_ASSOCIATE',
};
const ACCESSIBILITY: Record<string, string> = {
  Visual: 'VISUAL', Hearing: 'HEARING', Speech: 'SPEECH', Locomotor: 'LOCOMOTOR',
  Other: 'OTHER', 'Prefer not to say': 'PREFER_NOT_TO_SAY',
};

// Mirrors EMPLOYED in KnowYou.tsx — the backend requires employerName and
// industry for these employment statuses.
const EMPLOYED = new Set([
  'Salaried (private)',
  'Salaried (government/PSU)',
  'Self-employed professional',
  'Business owner',
]);

export const submitProfile = async (k: KnowYouDetails): Promise<ApplicationData> => {
  const id = requireApplicationId();
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    if (!application) throw new ApiError('NO_APPLICATION', 'No mock application exists.');
    return Promise.resolve(mockSaveApplication({ ...application, profileCompleted: true, currentStep: Math.max(application.currentStep, 7), status: 'PROFILE_COMPLETED' }));
  }
  const employed = EMPLOYED.has(k.employmentStatus);
  const wantsNominee = k.wantsNominee === 'YES';
  const isPep = k.isPep === 'YES';
  const taxResidentOther = k.taxResidentOther === 'YES';
  // The screen collects a single address; it is sent as both the permanent and
  // current address (currentAddressSameAsPermanent: true).
  const permanentAddress = {
    line1: k.addressLine1.trim(),
    line2: k.addressLine2.trim() || null,
    landmark: k.landmark.trim() || null,
    city: k.city.trim(),
    state: k.state,
    pincode: k.pinCode,
  };
  const body = {
    fatherName: k.fatherName.trim(),
    motherName: k.motherName.trim(),
    maritalStatus: MARITAL[k.maritalStatus] ?? 'SINGLE',
    spouseName: k.maritalStatus === 'Married' ? k.spouseName.trim() : null,

    permanentAddress,
    currentAddressSameAsPermanent: true,
    currentAddress: null,
    residenceType: RESIDENCE[k.residenceType] ?? 'OWNED',
    yearsAtCurrentAddress: YEARS[k.yearsAtAddress] ?? null,

    employmentStatus: EMPLOYMENT[k.employmentStatus] ?? 'SALARIED_PRIVATE',
    employerName: employed ? k.employerName.trim() : null,
    industry: employed ? (INDUSTRY[k.industry] ?? null) : null,
    designation: employed && k.designation.trim() ? k.designation.trim() : null,
    grossAnnualIncome: INCOME[k.annualIncome] ?? 'UPTO_2_5_LAKH',
    sourceOfFunds: k.sourcesOfFunds.map((s) => FUNDS[s] ?? 'OTHER'),
    otherSourceOfFunds: k.sourcesOfFunds.includes('Other') ? k.otherSource.trim() : null,

    accountPurpose: PURPOSE[k.purpose] ?? 'SAVINGS',
    expectedMonthlyDeposits: DEPOSITS[k.monthlyDeposits] ?? 'UPTO_50K',
    educationQualification: QUALIFICATION[k.qualification] ?? null,

    addNominee: wantsNominee,
    nominationType: wantsNominee ? k.nominationType : null,
    // Required when addNominee is false — the screen's "I understand the
    // implications of not nominating" checkbox.
    noNomineeAcknowledged: wantsNominee ? null : k.noNomineeConfirm,
    nominees: wantsNominee
      ? k.nominees.map((n) => ({
          fullName: n.fullName.trim(),
          relationship: RELATIONSHIP[n.relationship] ?? 'OTHER',
          dateOfBirth: n.dob,
          sharePercentage:
            k.nominationType === 'SIMULTANEOUS' ? parseFloat(n.sharePercent) || 0 : null,
          sameAddressAsApplicant: n.addressSameAsMine,
          guardianName: n.guardianName.trim() || null,
          guardianRelationship: n.guardianName.trim()
            ? (RELATIONSHIP[n.guardianRelationship] ?? null)
            : null,
        }))
      : [],

    politicallyExposedPerson: isPep,
    pepRelationship: isPep ? (PEP_REL[k.pepRelationship] ?? null) : null,
    pepPosition: isPep ? k.pepPosition.trim() : null,
    taxResidentOutsideIndia: taxResidentOther,
    taxResidencies: taxResidentOther
      ? k.taxRows.map((r) => ({
          country: r.country.trim(),
          tin: r.tin.trim() || null,
          noTinReason: r.tin.trim() ? null : r.noTinReason.trim() || null,
        }))
      : null,
    countryOfBirth: taxResidentOther ? k.countryOfBirth.trim() || 'India' : 'India',
    cityOfBirth: taxResidentOther ? k.cityOfBirth.trim() || null : null,
    citizenship: taxResidentOther ? k.citizenship.trim() || 'India' : 'India',
    usPerson: k.usPerson === 'YES',
    residentialStatus: k.residentialStatus,
    accessibilitySupport: k.accessibility ? ACCESSIBILITY[k.accessibility] : null,

    // Profile-level CKYC consent — the customer accepted the KYC declaration in
    // the product-screen pop-up. Version string per the integration guide.
    consents: [{ type: 'CKYC', accepted: true, documentVersion: '2026-01' }],
  };
  return wrap(() => post<ApplicationData>(`${OVR}/${id}/profile`, body));
};

// ---- Agent lookup (step 8) — replaces the hard-coded code list ----
export interface AgentInfo {
  agentCode: string;
  representativeName: string;
  channel: string;
  branchCode: string;
}

export const getAgent = async (agentCode: string): Promise<AgentInfo> => {
  if (!isRealApiEnabled()) return Promise.resolve({ agentCode, representativeName: 'Mock Relationship Manager', channel: 'BRANCH_STAFF', branchCode: 'MOCK-LOC-001' });
  return wrap(() => get<AgentInfo>(`/onboarding/agents/${encodeURIComponent(agentCode.trim())}`));
};

// ---- Final submit (step 8) ----
// UI channel labels → backend sourcing channel enum.
const ASSISTED_CHANNEL: Record<string, string> = {
  'Branch staff': 'BRANCH_STAFF',
  'Relationship Manager': 'RELATIONSHIP_MANAGER',
  'Direct Sales Agent (DSA)': 'DSA',
  'Business Correspondent (BC) agent': 'BC_AGENT',
  'Existing customer referral': 'REFERRAL',
  'Corporate salary tie-up': 'CORPORATE',
};

export const submitApplication = async (draft: OnboardingDraft): Promise<SubmitResult> => {
  const id = requireApplicationId();
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    if (!application?.signatureCaptured) throw new ApiError('ERR_006', 'Capture the signature before submitting.');
    const submittedAt = new Date().toISOString();
    const saved = mockSaveApplication({ ...application, status: 'SUBMITTED', currentStep: 8, submittedAt, updatedAt: submittedAt });
    return Promise.resolve({ reference: saved.applicationReference, customerId: saved.customerId, product: (draft.product ?? saved.accountType) as SubmitResult['product'], mobileMasked: saved.maskedMobile, firstName: saved.fullName.split(' ')[0] ?? '', submittedAt, handover: '' });
  }
  const application = await getApplicationById(id);
  if (!application.signatureCaptured) {
    throw new ApiError('ERR_006', 'Capture the signature before submitting.');
  }
  const a = draft.assistedBy;
  const assisted = a.wasAssisted === 'YES';
  // Contract: an unassisted application posts { "assisted": false } only.
  const body = assisted
    ? {
        assisted: true,
        assistedChannel: ASSISTED_CHANNEL[a.channel] ?? undefined,
        assistedBy: a.representativeName.trim() || undefined,
        agentCode: a.agentCode.trim() || undefined,
        branchCode: a.branchCode.trim() || undefined,
        referralCode: a.referral.trim() || undefined,
        corporateCode: a.corporateCode.trim() || undefined,
        promoCode: a.promoCode.trim() || undefined,
      }
    : { assisted: false };
  const data = await wrap(() => post<SubmitData>(`${OVR}/${id}/submit`, body));
  return {
    reference: data.applicationReference,
    customerId: data.customerId,
    product: (draft.product ?? 'SAVINGS') as SubmitResult['product'],
    mobileMasked: data.registeredMobile,
    firstName: (data.applicantName || draft.personal.firstAndMiddleNames).split(' ')[0] || '',
    submittedAt: data.submittedAt,
    handover: '',
  };
};

// ---- Tracking ----
export interface BusinessProfile {
  entityType: string;
  legalName: string;
  tradeName?: string | null;
  gstin?: string | null;
  gstinExempt?: boolean;
  gstinExemptReason?: string | null;
  udyamNumber?: string | null;
  businessPan: string;
  incorporationDate: string;
  natureOfBusiness: string;
  annualTurnoverRange: string;
  businessAddressLine1: string;
  businessAddressLine2?: string | null;
  businessCity: string;
  businessState: string;
  businessPincode: string;
  yearsInBusiness: number;
}

export const getBusinessProfile = async (applicationId: string): Promise<BusinessProfile> => {
  if (!isRealApiEnabled()) throw new ApiError('NOT_AVAILABLE', 'Business profile is not used in the mock flow.');
  return wrap(() => get<BusinessProfile>(`${OVR}/${encodeURIComponent(applicationId)}/business-profile`));
};

export const saveBusinessProfile = async (
  applicationId: string,
  body: BusinessProfile
): Promise<BusinessProfile> => {
  if (!isRealApiEnabled()) return Promise.resolve(body);
  return wrap(() => post<BusinessProfile>(`${OVR}/${encodeURIComponent(applicationId)}/business-profile`, body));
};

export interface TrackApplicationInput {
  mobileNumber?: string;
  aadhaarNumber?: string;
  applicationReference?: string;
}

export interface ApplicationRecovery {
  recoveryId: string;
  maskedDestination: string;
  deliveryMethod: string;
  expiresAt: string;
}

export const trackApplication = async (
  input: TrackApplicationInput
): Promise<ApplicationRecovery> => {
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    if (!application || (input.applicationReference && application.applicationReference !== input.applicationReference)) throw new ApiError('NOT_FOUND', 'Application not found.');
    return Promise.resolve({ recoveryId: `mock-recovery-${application.applicationId}`, maskedDestination: application.maskedMobile, deliveryMethod: 'SMS', expiresAt: new Date(Date.now() + 300000).toISOString() });
  }
  return wrap(() => post<ApplicationRecovery>(`${OVR}/track`, input));
};

export const findApplication = async (
  input: TrackApplicationInput
): Promise<ApplicationRecovery> => trackApplication(input);

export interface VerifyApplicationRecoveryInput {
  recoveryId: string;
  otp: string;
}

export const verifyApplicationRecovery = async (
  input: VerifyApplicationRecoveryInput
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) {
    if (input.otp !== '123456') throw new ApiError('AUTH_012', 'The code you entered is incorrect. Please try again.');
    const application = mockReadApplication();
    if (!application) throw new ApiError('NOT_FOUND', 'Application not found.');
    setApplicationId(application.applicationId);
    return application;
  }
  const data = await wrap(() => post<ApplicationData>(`${OVR}/track/verify`, input));
  setApplicationId(data.applicationId);
  return data;
};

export interface AccountOpeningInput {
  accountType: string;
  branchCode: string;
  currency: string;
  initialDeposit: number;
  remarks?: string;
}

export interface AccountOpeningResult {
  accountId: string;
  accountNumber: string;
  customerId: string;
  accountType: string;
  ifsc: string;
  branchName: string;
  currency: string;
  status: string;
  openingDate: string;
}

export const openCustomerAccount = async (
  customerId: string,
  input: AccountOpeningInput
): Promise<AccountOpeningResult> => {
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    const accountNumber = application?.accountNumber ?? `1000000${String(Date.now()).slice(-5)}`;
    return Promise.resolve({ accountId: `mock-account-${customerId}`, accountNumber, customerId, accountType: input.accountType, ifsc: 'HDFC0000001', branchName: 'MG Road Branch', currency: input.currency, status: 'ACTIVE', openingDate: new Date().toISOString().slice(0, 10) });
  }
  return wrap(() => post<AccountOpeningResult>(`/customers/${encodeURIComponent(customerId)}/account-opening`, input));
};

export interface UpdateApplicationInput {
  salutation?: string;
  firstAndMiddleName?: string;
  surname?: string;
  dateOfBirth?: string;
  gender?: string;
  aadhaarNumber?: string;
  panNumber?: string;
  mobileNumber?: string;
  email?: string;
  residentOfIndia?: boolean;
}

export const updateApplication = async (
  applicationId: string,
  changes: UpdateApplicationInput
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) {
    const current = mockReadApplication();
    if (!current || current.applicationId !== applicationId) throw new ApiError('NOT_FOUND', 'Application not found.');
    return Promise.resolve(updateMockOnboardingApplication({
      ...('firstAndMiddleName' in changes ? { fullName: `${changes.firstAndMiddleName ?? ''} ${changes.surname ?? ''}`.trim() } : {}),
    }));
  }
  return wrap(() => patch<ApplicationData>(`${OVR}/${encodeURIComponent(applicationId)}`, changes));
};

export const abandonApplication = async (applicationId: string): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) {
    const data = updateMockOnboardingApplication({ status: 'ABANDONED' });
    setApplicationId(null);
    return data;
  }
  const data = await wrap(() => del<ApplicationData>(`${OVR}/${encodeURIComponent(applicationId)}`));
  if (getApplicationId() === applicationId) setApplicationId(null);
  return data;
};

export interface UpiAvailability {
  upiId: string;
  available: boolean;
  message: string;
}

export const checkUpiAvailability = async (upiId: string): Promise<UpiAvailability> => {
  if (!isRealApiEnabled()) return Promise.resolve({ upiId, available: true, message: 'UPI ID is available.' });
  return wrap(() => get<UpiAvailability>(`${OVR}/upi-id/availability`, { params: { upiId } }));
};

export interface Employer {
  [key: string]: unknown;
}

export const getEmployers = async (): Promise<Employer[]> => {
  if (!isRealApiEnabled()) return Promise.resolve([{ id: 'mock-employer-1', name: 'NovaBank Mock Employer' }]);
  return wrap(() => get<Employer[]>(`${OVR}/employers`));
};

export interface AccountTypeInfo {
  [key: string]: unknown;
}

export const getAccountTypes = async (): Promise<AccountTypeInfo[]> => {
  if (!isRealApiEnabled()) return Promise.resolve([{ code: 'SAVINGS', name: 'Savings Account' }, { code: 'CURRENT', name: 'Current Account' }]);
  return wrap(() => get<AccountTypeInfo[]>(`${OVR}/account-types`));
};

export const getApplicationById = async (applicationId: string): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) {
    const application = mockReadApplication();
    if (!application || application.applicationId !== applicationId) throw new ApiError('NOT_FOUND', 'Application not found.');
    return Promise.resolve(application);
  }
  return wrap(() => get<ApplicationData>(`${OVR}/${encodeURIComponent(applicationId)}`));
};

export type ReviewVideoKycOutcome =
  | 'SUCCESS'
  | 'POOR_CONNECTION'
  | 'PAN_UNCLEAR'
  | 'FACE_MISMATCH'
  | 'WRONG_ANSWER';

export interface ReviewVideoKycRejectionInput {
  reason: string;
}

export interface ReviewVideoKycStatus {
  state: string;
  attempts: number;
  joinLink?: string | null;
  linkIssuedAt?: string | null;
  linkExpiresAt?: string | null;
  scheduledAt?: string | null;
  lastOutcome?: ReviewVideoKycOutcome | null;
  rejectionReason?: string | null;
  question?: string | null;
  linkExpired?: boolean;
}

const reviewVideoKycPath = (applicationId: string): string =>
  `/onboarding/review/${encodeURIComponent(applicationId)}/video-kyc`;

export const rejectReviewVideoKyc = async (
  applicationId: string,
  input: ReviewVideoKycRejectionInput,
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) return updateMockOnboardingApplication({ status: 'KYC_REJECTED', lifecycleState: 'REJECTED' });
  return wrap(() => post<ApplicationData>(`${reviewVideoKycPath(applicationId)}/reject`, input));
};

export const expireReviewVideoKycLink = async (
  applicationId: string,
): Promise<ReviewVideoKycStatus> => {
  if (!isRealApiEnabled()) return { state: 'FAILED', attempts: 1, linkExpired: true };
  return wrap(() => post<ReviewVideoKycStatus>(`${reviewVideoKycPath(applicationId)}/expire-link`));
};

export const completeReviewVideoKyc = async (
  applicationId: string,
  outcome?: ReviewVideoKycOutcome,
): Promise<ReviewVideoKycStatus> => {
  if (!isRealApiEnabled()) {
    const result = outcome ?? 'SUCCESS';
    if (result === 'SUCCESS') updateMockOnboardingApplication({ status: 'KYC_COMPLETED', currentStep: 9 });
    return { state: result === 'SUCCESS' ? 'DONE_AUDIT_PENDING' : 'REJECTED', attempts: 1, lastOutcome: result };
  }
  return wrap(() => post<ReviewVideoKycStatus>(`${reviewVideoKycPath(applicationId)}/complete`, undefined, { params: outcome ? { outcome } : undefined }));
};

export const approveReviewVideoKyc = async (
  applicationId: string,
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) return updateMockOnboardingApplication({ status: 'KYC_APPROVED', lifecycleState: 'ACTIVE_FULL_KYC' });
  return wrap(() => post<ApplicationData>(`${reviewVideoKycPath(applicationId)}/approve`));
};

export type ReviewReturnStep =
  | 'PERSONAL_DETAILS'
  | 'CONTACT_VERIFICATION'
  | 'DOCUMENTS'
  | 'SIGNATURE'
  | 'PROFILE'
  | 'DIGITAL_BANKING'
  | 'VIDEO_KYC';

export interface ReviewReturnInput {
  reason: string;
}

export const startApplicationReview = async (
  applicationId: string,
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) return updateMockOnboardingApplication({ status: 'UNDER_VERIFICATION', lifecycleState: 'UNDER_VERIFICATION' });
  return wrap(() => post<ApplicationData>(`/onboarding/review/${encodeURIComponent(applicationId)}/start`));
};

export const unassignApplicationReview = async (
  applicationId: string,
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) return updateMockOnboardingApplication({ status: 'SUBMITTED', lifecycleState: 'PENDING' });
  return wrap(() => post<ApplicationData>(`/onboarding/review/${encodeURIComponent(applicationId)}/unassign`));
};

export const returnApplicationForReview = async (
  applicationId: string,
  input: ReviewReturnInput,
  step: ReviewReturnStep = 'DOCUMENTS',
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) return updateMockOnboardingApplication({ status: 'RETURNED', currentStep: step === 'DOCUMENTS' ? 5 : 7 });
  return wrap(() => post<ApplicationData>(`/onboarding/review/${encodeURIComponent(applicationId)}/return`, input, { params: { step } }));
};

export const reopenApplicationReview = async (
  applicationId: string,
  reason?: string,
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) return updateMockOnboardingApplication({ status: 'SUBMITTED', lifecycleState: 'PENDING' });
  return wrap(() => post<ApplicationData>(`/onboarding/review/${encodeURIComponent(applicationId)}/reopen`, undefined, { params: reason ? { reason } : undefined }));
};

export const getApplicationByReference = async (
  reference: string,
  mobileNumber?: string
): Promise<TrackedApplication> => {
  if (!isRealApiEnabled()) {
    const data = mockReadApplication();
    if (!data || data.applicationReference !== reference) throw new ApiError('NOT_FOUND', 'Application not found.');
    return { applicationId: data.applicationId, reference: data.applicationReference, customerId: data.customerId, product: data.accountType, status: data.lifecycleState ?? data.status, currentStep: data.currentStep, fullName: data.fullName, firstName: data.fullName.split(' ')[0] ?? '', maskedMobile: data.maskedMobile, maskedEmail: data.maskedEmail, mobileVerified: data.mobileVerified, emailVerified: data.emailVerified, signatureCaptured: data.signatureCaptured, profileCompleted: data.profileCompleted, uploadedDocuments: data.uploadedDocuments, submittedAt: data.submittedAt, accountNumber: data.accountNumber, ifsc: data.ifsc, branchName: data.branchName, activatedAt: data.activatedAt };
  }
  const registeredMobile = mobileNumber?.replace(/\D/g, '') || getSession()?.mobileNumber;
  if (!registeredMobile) {
    throw new ApiError('MOBILE_REQUIRED', 'Enter the registered mobile number to view this application.');
  }
  const data = await wrap(() =>
    get<ApplicationData>(`${OVR}/by-reference/${encodeURIComponent(reference)}`, {
      params: { mobileNumber: registeredMobile },
    })
  );
  return {
    applicationId: data.applicationId,
    reference: data.applicationReference,
    customerId: data.customerId,
    product: data.accountType,
    status: data.lifecycleState ?? data.status,
    currentStep: data.currentStep,
    fullName: data.fullName,
    firstName: (data.fullName || '').split(' ')[0] || '',
    maskedMobile: data.maskedMobile,
    maskedEmail: data.maskedEmail,
    mobileVerified: data.mobileVerified,
    emailVerified: data.emailVerified,
    signatureCaptured: data.signatureCaptured,
    profileCompleted: data.profileCompleted,
    uploadedDocuments: data.uploadedDocuments ?? [],
    submittedAt: data.submittedAt,
    accountNumber: data.accountNumber,
    ifsc: data.ifsc,
    branchName: data.branchName,
    activatedAt: data.activatedAt,
  };
};

// Alias used by ApplicantHome.
export const getApplicantStatus = getApplicationByReference;
